/**
 * Lightweight inline markdown renderer for agent chat bubbles.
 * Handles common patterns without pulling in a full markdown library:
 *   - **bold** / __bold__
 *   - *italic* / _italic_
 *   - `code`
 *   - [link](url)
 *   - Line breaks
 *   - Bulleted lists (- item or * item)
 *   - Numbered lists (1. item)
 */

import { useMemo } from 'react';

interface Props {
  content: string;
  className?: string;
}

export function SimpleMarkdown({ content, className = '' }: Props) {
  const rendered = useMemo(() => parseMarkdown(content), [content]);
  return (
    <div
      className={`simple-markdown ${className}`}
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function parseMarkdown(raw: string): string {
  if (!raw) return '';

  // Split into lines for list processing
  const lines = raw.split('\n');
  const result: string[] = [];
  let inList: 'ul' | 'ol' | null = null;

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Check for list items
    const ulMatch = line.match(/^(\s*)[-*]\s+(.*)$/);
    const olMatch = line.match(/^(\s*)\d+\.\s+(.*)$/);

    if (ulMatch) {
      if (inList !== 'ul') {
        if (inList) result.push(inList === 'ol' ? '</ol>' : '</ul>');
        result.push('<ul class="list-disc pl-5 my-1">');
        inList = 'ul';
      }
      result.push(`<li class="my-0.5">${parseInline(escapeHtml(ulMatch[2]))}</li>`);
      continue;
    }

    if (olMatch) {
      if (inList !== 'ol') {
        if (inList) result.push(inList === 'ol' ? '</ol>' : '</ul>');
        result.push('<ol class="list-decimal pl-5 my-1">');
        inList = 'ol';
      }
      result.push(`<li class="my-0.5">${parseInline(escapeHtml(olMatch[2]))}</li>`);
      continue;
    }

    // Close list if we're not in a list item
    if (inList) {
      result.push(inList === 'ol' ? '</ol>' : '</ul>');
      inList = null;
    }

    // Skip empty lines but preserve paragraph breaks
    if (!line.trim()) {
      result.push('<br/>');
      continue;
    }

    // Process headers (## Header)
    const headerMatch = line.match(/^(#{1,3})\s+(.*)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const text = parseInline(escapeHtml(headerMatch[2]));
      const sizes = ['text-base font-semibold', 'text-sm font-semibold', 'text-xs font-semibold'];
      result.push(`<p class="${sizes[level - 1] || sizes[2]} mt-2 mb-1">${text}</p>`);
      continue;
    }

    // Regular paragraph
    result.push(`<p class="my-0.5">${parseInline(escapeHtml(line))}</p>`);
  }

  // Close any open list
  if (inList) {
    result.push(inList === 'ol' ? '</ol>' : '</ul>');
  }

  return result.join('');
}

function parseInline(text: string): string {
  // Bold: **text** or __text__
  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/__([^_]+)__/g, '<strong>$1</strong>');

  // Italic: *text* or _text_ (but not inside words)
  text = text.replace(/(?<!\w)\*([^*]+)\*(?!\w)/g, '<em>$1</em>');
  text = text.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '<em>$1</em>');

  // Inline code: `code`
  text = text.replace(
    /`([^`]+)`/g,
    '<code class="px-1 py-0.5 rounded text-[11px]" style="background: var(--vscode-bg-hover)">$1</code>',
  );

  // Links: [text](url)
  text = text.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="underline" style="color: var(--ggj-accent, #10B981)">$1</a>',
  );

  return text;
}
